/**
 * Converts the stored Amplitude experiments into the shape the game-client
 * expects on `payload.experiments`, e.g. `{ gameSwitchEnabled: 'on' }`, by:
 *  1. renaming raw Amplitude keys to client keys (e.g. `game-switch-enable` ->
 *     `gameSwitchEnabled`), and
 *  2. unwrapping each variant `{ value }` down to its `value`.
 *
 * Unknown keys and already-flat values are returned unchanged.
 */
const EXPERIMENTS_KEY_MAP: Record<string, string> = {
  'site-wide-jackpots': 'swjEnabled',
  'net-position-lnw-handling': 'netPositionLnWHandling',
  'game-switch-enable': 'gameSwitchEnabled',
  game_launcher_um_enable: 'gameLauncherUMEnabled',
  net_position_enabled: 'netPositionEnabled',
  net_position_force: 'netPositionForce',
  net_position_game_switch_not_available: 'netPositionGameSwitchNotAvailable',
  'casino-in-game-wallet-v2': 'casinoInGameWalletV2Enabled',
};

/** Raw Amplitude flag keys the game-client consumes from `payload.experiments`. */
export const CLIENT_EXPERIMENT_FLAG_KEYS = Object.keys(EXPERIMENTS_KEY_MAP);

const extractVariantValue = (variant: unknown): unknown => {
  if (variant && typeof variant === 'object' && 'value' in variant) {
    return (variant as { value?: unknown }).value;
  }
  return variant;
};

export const mapExperimentsForClient = (
  experiments: Record<string, unknown> | null | undefined,
): Record<string, unknown> => {
  if (!experiments || typeof experiments !== 'object') {
    return {};
  }

  return Object.keys(experiments).reduce<Record<string, unknown>>(
    (acc, rawKey) => {
      const key = EXPERIMENTS_KEY_MAP[rawKey] ?? rawKey;
      acc[key] = extractVariantValue(experiments[rawKey]);
      return acc;
    },
    {},
  );
};
